// EdAI MedSchool page content. House rules: no em-dashes; the clinical skills
// product is named only Practicums; the competency framework is described as
// it is implemented, without academic attribution; EdAI provides evidence,
// review bodies decide. Statuses stay honest: roadmap is never dressed as
// shipped.

// The interactive specimen: the interpret-then-compare loop. The student
// commits a timestamped read BEFORE consulting AI, then reconciles the AI's
// draft item by item, and every scorable moment writes one append-only event.
export const loopSpecimen = {
  header: 'PreRounds \u00b7 06:41 \u00b7 before rounds',
  chart: {
    patient: 'Ms. Alvarez, 58. Community-acquired pneumonia, hospital day 2.',
    data: 'Overnight: afebrile since 02:00. Creatinine 1.8 this morning, up from 1.1 on admission. On ceftriaxone and azithromycin. Urine output borderline.',
  },
  commitPrompt: 'Commit your read first. It is timestamped before any AI is consulted.',
  commitOptions: [
    { id: 'renal', text: 'The leading issue today is worsening renal function', quality: 'strong' },
    { id: 'fever', text: 'The leading issue today is the persistent pneumonia itself', quality: 'reasonable' },
    { id: 'dispo', text: 'The leading issue today is discharge planning', quality: 'premature' },
  ],
  aiDraft:
    'AI draft assessment: "Day 2 of CAP, clinically improving and afebrile overnight. Renal function remains stable. Continue current antibiotic regimen at standard dosing; anticipate discharge planning within 48 hours."',
  reconcilePrompt: 'Reconcile the renal line against the chart. Every accept, modify, or override needs a rationale.',
  reconcileOptions: [
    {
      id: 'accept',
      label: 'Accept',
      text: 'Carry the AI\u2019s renal line into your note as written',
      outcome: 'accepted',
    },
    {
      id: 'modify',
      label: 'Modify',
      text: 'Correct it: creatinine 1.1 to 1.8 is not stable; review renally dosed medications and recheck',
      outcome: 'caught',
    },
    {
      id: 'override',
      label: 'Override',
      text: 'Replace it entirely with your own renal assessment and plan',
      outcome: 'caught',
    },
  ],
  rationales: {
    accepted: 'Rationale recorded: "consistent with overnight course." The chart disagrees.',
    caught: 'Rationale recorded: "creatinine rose 0.7 from admission; the draft\u2019s stability claim is not supported by the chart."',
  },
};

export const problems = [
  {
    title: 'Students use AI clinically; nobody observes it',
    text: 'Students on clerkships consult general-purpose AI while pre-rounding: differentials, draft assessments, protocol checks, in a browser tab outside any institutional system. The school cannot see it, the student gets no feedback on whether their use was skilful or credulous, and there is no record the school taught anything about it.',
  },
  {
    title: 'There is no standard instrument for AI competency',
    text: 'Competency-based education runs on entrustment: can this learner be trusted to do this thing, at this level of supervision? For clinical AI use, schools that want to demonstrate AI education to a review body have had nothing structured to show.',
  },
  {
    title: 'Evidence is scattered; accreditation is manual',
    text: 'A question bank here, a simulation centre there, an LMS somewhere else, none sharing a scoring vocabulary. Curriculum maps and cohort attainment tables get assembled by hand from institutional memory at review time.',
  },
];

export const applications = [
  {
    name: 'PreRounds',
    cadence: 'Clinical AI safety training',
    text: 'Learners work realistic cases alongside an AI assistant while TheDude, a real-time intervention engine, watches for the failure modes that actually hurt patients: contaminating the model with a premature diagnosis, dropping socioeconomic context between queries, trusting confident output without probing its uncertainty, and missing the moment the AI drifts outside its competence.',
  },
  {
    name: 'MedSchool Suite',
    cadence: 'Continuous coursework and administration',
    text: 'The institutional backbone: content library, LMS with courses and curricula, a USMLE-aligned question bank, the Failure Lab, discussion groups, virtual classrooms, oral-exam practice, a lecture-processing tool, a research notebook, the Competency Portfolio, and the Accreditation Dashboard. Administrators switch modules on and off per institution.',
  },
  {
    name: 'Practicums',
    cadence: 'Periodic high-fidelity encounters',
    text: 'Simulated clinical encounters with spoken conversation and examination findings. In the AI-specific scenario types, the student is shown output from a clinical AI tool that may be right, plausibly wrong, or incomplete, and their handling of it is scored. Native integration: encounters write into the same record as everything else.',
  },
];

export const threeSentences = [
  'One person, one identity, three applications.',
  'Everything scorable becomes an unchangeable event in one ledger.',
  'Portfolios and accreditation reports are derived from that ledger, not typed in by hand.',
];

export const framework = {
  intro:
    'The platform ships a concrete competency instrument: the 13 AAMC Core EPAs for Entering Residency alongside 12 AI-specific milestones across three learning domains, every one scored on the same 0 to 4 entrustment scale used elsewhere in medical education, from not ready through direct and indirect supervision to independent. It is seeded into the database identically for every institution, so schools measure with the same instrument and can compare.',
  elements: [
    { key: 'P', name: 'Psychomotor', text: 'The doing: appraising an algorithm, constructing a clinically effective prompt, integrating a tool at the point of care, explaining an AI-informed decision to a patient.' },
    { key: 'A', name: 'Affective', text: 'The judgement: calibrating trust, identifying bias, preserving patient-centred values, recognizing AI failure.' },
    { key: 'C', name: 'Cognitive', text: 'The knowledge: AI fundamentals, health data science, strengths and limitations, ethics and law.' },
    { key: 'E', name: 'Embedded', text: 'The delivery principle: competencies woven longitudinally into existing courses and rotations, never a standalone AI course bolted on.' },
  ],
  milestonesNote:
    'Twelve milestones span the three learning domains, from AI fundamentals and algorithm appraisal to trust calibration, bias identification, and AI failure recognition, each tagged with which applications can generate evidence for it.',
};

export const ledger = [
  {
    title: 'Append-only, by design',
    text: 'Every scorable moment across the three applications writes one assessment event to a shared ledger, in one schema. Events can never be edited. The record a school shows a review body is the record that was written at the moment of assessment.',
  },
  {
    title: 'Portfolios that rebuild themselves',
    text: 'A student\u2019s competency portfolio is derived from the ledger and rebuilds automatically when new evidence arrives: a heatmap for the student, a cohort view with at-risk flags for faculty.',
  },
  {
    title: 'The Accreditation Dashboard',
    text: 'Reads the live event store and renders a curriculum map (competency domain by academic phase, colour-coded assessed, taught-but-unassessed, or empty), per-milestone cohort achievement with CSV export, an assessment-instrument inventory, and a printable report laid out for review.',
  },
  {
    title: 'Evidence, not endorsement',
    text: 'EdAI produces the evidence trail. What counts as sufficient is the review body\u2019s decision, and we never imply otherwise. The dashboard\u2019s job is to make the honest answer to "show us how you taught this" take minutes instead of months.',
  },
];

export const failureLab = {
  heading: 'The Failure Lab',
  body: 'Students study how clinical AI fails, deliberately: hallucination, bias, overreach, and the plausible-but-wrong. Recognizing failure is treated as an examinable skill, scored by faculty against a rubric, and the scores land in the same ledger as everything else, feeding the trust-calibration and bias-identification milestones.',
};

export const scenario = {
  heading: 'The Brownsville scenario',
  text: 'An uninsured seven-year-old presents with otitis externa. The AI keeps recommending Ciprodex, a prescription the family cannot afford, and the learner either catches it or does not. Every session is scored against a clinical AI competency rubric, progress rolls into a competency profile, and completed training is CME-eligible: supervising AI becomes a skill that is measured and credentialed, not assumed.',
};

export const facultyAngle = {
  heading: 'Faculty adopt it because it saves them time',
  body: 'Faculty will not attend AI workshops for their own sake, so the faculty-facing entry point is labour saving, not training: generate blueprint-mapped questions from a topic, an article, or a lecture transcript; turn a recorded lecture into a transcript, extracted slides, and study materials; get AI-assisted first-pass grading against the instructor\u2019s own rubric. AI literacy arrives as a by-product of use.',
};

export const screenshots = [
  { src: '/screenshots/prerounds.jpg', alt: 'PreRounds case view', caption: 'A PreRounds case workup' },
  { src: '/screenshots/pre-dash.jpg', alt: 'PreRounds dashboard', caption: 'The student dashboard' },
  { src: '/screenshots/pre-aiter.jpg', alt: 'AI interaction view', caption: 'Clinical AI use, captured and assessed' },
];

export const video = {
  embed: null, // paste https://www.youtube.com/embed/VIDEO_ID when ready
  title: 'See PreRounds in action',
  text: 'A learner works a case with the AI assistant, TheDude intervenes on a real failure mode, and the session lands in the competency record.',
};

export const faq = [
  {
    q: 'Does this satisfy our accreditor?',
    a: 'That is the review body\u2019s call, and we will not pretend otherwise. What the platform does is make your answer demonstrable: a concrete instrument, an append-only evidence ledger, and an accreditation dashboard that derives the curriculum map, cohort attainment, and a printable report from live data instead of retrospective spreadsheets.',
  },
  {
    q: 'Is this another standalone AI course?',
    a: 'Deliberately not. The delivery principle is embedded: competencies are woven longitudinally into existing courses, rotations, and daily pre-rounding, and the standalone-course model is what the framework exists to avoid.',
  },
  {
    q: 'Why would students use the sanctioned tool instead of a browser tab?',
    a: 'Because the workflow gives them something the tab cannot: their own committed read, timestamped before the AI\u2019s, feedback on whether their use was skilful, and a portfolio of evidence that follows them. The sequence is the pedagogy: commit first, consult second, reconcile with a rationale.',
  },
  {
    q: 'What does the school own and where does data live?',
    a: 'Each institution gets its own cloud project; the institution\u2019s data lives only in that project. The applications are served from a CDN with nothing to install on school servers or desktops, and one institutional sign-in spans all three applications.',
  },
  {
    q: 'How mature is the platform?',
    a: 'The suite did not start from zero: the LMS, question bank, discussions, classrooms, and authoring tools were production code before the medical-education pivot, which is why the breadth is unusual for the product\u2019s age. Where something is in development or on the roadmap, our documentation says so in those words, and so will we.',
  },
];
